import { type FC, type JSX, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useCreateAppointment } from '../../services/query/appointment/appointment';
import { toast } from '../../lib/utils/toast';
import { TIME_SLOTS } from '../../enums';
import { getErrorMessage, toAppointmentDatetimeISO } from '../../lib/utils/helpers';
import Button from '../shared/Button';
import DatePicker from '../shared/DatePicker';
import TimePicker from '../shared/TimePicker';
import FormField from '../shared/FormField';
import Input from '../shared/Input';
import Select from '../shared/Select';
import Textarea from '../shared/Textarea';

const formatTime = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour.toString().padStart(2, '0')}:${minutes} ${period}`;
};

interface AppointmentFormProps {
    onClose: () => void;
  initialDate: string;
  initialTime: string;
}

const AppointmentForm: FC<AppointmentFormProps> = ({
    onClose,
    initialDate,
    initialTime,
}:AppointmentFormProps): JSX.Element => {
    const [patientName, setPatientName] = useState('');
    const [patientPhone, setPatientPhone] = useState('');
    const [reason, setReason] = useState('');
    const [duration, setDuration] = useState(30);

    const todayISODate = useMemo(() => new Date().toISOString().split('T')[0], []);

    const computedInitialDate = useMemo(() => {
      return initialDate || todayISODate;
    }, [initialDate, todayISODate]);

    const computedInitialTime = useMemo(() => {
      const candidate = initialTime || '09:00';
      return (TIME_SLOTS as readonly string[]).includes(candidate) ? candidate : '09:00';
    }, [initialTime]);

    const [selectedDate, setSelectedDate] = useState<string>(computedInitialDate);
    const [selectedTime, setSelectedTime] = useState<string>(computedInitialTime);

    useEffect(() => {
      setSelectedDate(computedInitialDate);
      setSelectedTime(computedInitialTime);
    }, [computedInitialDate, computedInitialTime]);

    const { mutate: createAppointment, isPending } = useCreateAppointment();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!patientName || !patientPhone || !reason || !selectedDate || !selectedTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    const appointmentDatetimeISO = toAppointmentDatetimeISO(selectedDate, selectedTime);

    const payload = {
      patient_name: patientName,
      patient_phone: patientPhone,
      reason: reason,
      appointment_datetime: appointmentDatetimeISO,
      duration_minutes: duration
    };

    createAppointment(payload, {
      onSuccess: () => {
        toast.success('Appointment created successfully!');
        onClose();
      },
      onError: (error: unknown) => {
        toast.error(getErrorMessage(error, 'Failed to create appointment'));
      }
    });
  };

  return (
    <div className="fixed top-[-30px] inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">New Appointment</h3>
            <p className="text-sm text-gray-600">Schedule a new patient appointment</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <FormField label="Patient Name" required>
            <Input
              type="text"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              placeholder="Enter patient name"
              required
              disabled={isPending}
            />
          </FormField>

          <FormField label="Patient Phone" required>
            <Input
              type="tel"
              value={patientPhone}
              onChange={(e) => setPatientPhone(e.target.value)}
              placeholder="e.g., 8148482735"
              required
              disabled={isPending}
            />
          </FormField>

          <FormField label="Reason for Appointment" required>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Describe the reason for the appointment..."
              required
              disabled={isPending}
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <DatePicker
              label="Date"
              required
              value={selectedDate}
              onChange={setSelectedDate}
              min={todayISODate}
              disabled={isPending}
            />

            <TimePicker
              label="Time"
              required
              value={selectedTime}
              onChange={setSelectedTime}
              options={TIME_SLOTS}
              getOptionLabel={formatTime}
              disabled={isPending}
            />
          </div>

          <FormField label="Duration (minutes)">
            <Select
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              disabled={isPending}
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </Select>
          </FormField>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={isPending} disabled={isPending}>
              {isPending ? 'Creating...' : 'Create Appointment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentForm;